import React, { useEffect, useRef } from 'react';
import { Layer, TextProps, AssetType, TextRef } from '../../types/typing.ts';
import { Container, Application } from 'pixi.js';
import { renderText } from './utils/renderText.ts';
import { updateCircleVisibility } from './utils/recordAnimation.ts';
import { isBackgroundRecordingApp } from './utils/backgroundRecordingState.ts';

interface TextRendererProps {
  app: Application | null;
  selectedTexts: TextProps[] | undefined;
  width: number;
  height: number;
  layers: Layer[] | undefined;
  textRef: React.MutableRefObject<Map<string, TextRef>>;
  onUpdateText: (id: string, textProps: Partial<TextProps>) => void;
  onAssetSelection: (id: string, assetName: AssetType) => void;
  isRecording: boolean;
  recordingStartTime: number | null;
  selectedMenu: string | null | undefined;
}

const TextRenderer: React.FC<TextRendererProps> = ({
  app,
  selectedTexts,
  width,
  height,
  layers,
  textRef,
  onUpdateText,
  onAssetSelection,
  isRecording,
  recordingStartTime,
  selectedMenu,
}) => {
  const selectedMenuRef = useRef<string | null | undefined>(null);
  const isRecordingRef = useRef<boolean | null | undefined>(null);
  const recordingStartTimeRef = useRef<number | null | undefined>(null);

  useEffect(() => {
    selectedMenuRef.current = selectedMenu;
  }, [selectedMenu]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    recordingStartTimeRef.current = recordingStartTime;
  }, [recordingStartTime]);

  useEffect(() => {
    if (!app) return;

    selectedTexts?.forEach(async (selectedText: TextProps) => {
      const text = Array.from(textRef.current.values()).find(
        (textRefItem) => textRefItem.id === selectedText.id
      );

      const hasValidContainer = Boolean(
        text?.textContainer &&
          !text.textContainer.destroyed &&
          (!text.textContainer.parent || text.textContainer.parent === app.stage)
      );

      if (text?.textInput && hasValidContainer) {
        text.textContainer.interactive = true;
        text.textContainer.eventMode = 'static';
        text.textContainer.cursor = 'text';
        Object.assign(text.textInput.style, {
          fontSize: selectedText.fontProperties.fontSize,
          fontFamily: selectedText.fontProperties.fontFamily,
          fontStyle: selectedText.fontProperties.fontStyle,
          stroke: selectedText.fontProperties.fontColor,
          fill: selectedText.fontProperties.fontColor,
          fontWeight: selectedText.fontProperties.fontWeight,
        });
        text.textInput.text = selectedText.text;
        text.textContainer.position = selectedText.position;
        text.position = selectedText.position;
        if (!text.textContainer.parent) {
          app.stage.addChild(text.textContainer);
        }
        textRef.current.set(selectedText.id, text);
      } else {
        // Stale refs can survive app reload and are no longer interactive on the new stage.
        if (text) {
          text.destroy?.();
          textRef.current.delete(selectedText.id);
        }

        const textInputFieldContainer: Container | undefined = await renderText(
          app,
          selectedText,
          onUpdateText,
          onAssetSelection,
          textRef,
          isRecordingRef,
          recordingStartTimeRef,
          selectedMenuRef
        );

        if (textInputFieldContainer)
          app?.stage.addChild(textInputFieldContainer);
      }
      /*const indicesToRemove = Array.from(textRef.current.keys()).filter(
        (index) => index >= selectedTexts.length
      );

      indicesToRemove.forEach((index) => {
        const char = textRef.current.get(index);
        if (char) {
          char.destroy?.();
          textRef.current.delete(index);
        }
      });*/
    });
  }, [app, selectedTexts, width, height]);

  useEffect(() => {
    if (!app) return;

    return () => {
      if (isBackgroundRecordingApp(app)) {
        return;
      }

      textRef.current.forEach((text) => {
        text.destroy?.();
      });
      textRef.current.clear();
    };
  }, [app, textRef]);

  useEffect(() => {
    textRef.current?.forEach((textRef) => {
      updateCircleVisibility(textRef.pathCircles, selectedMenuRef);
    });
  }, [selectedMenu]);

  useEffect(() => {
    if (layers) {
      textRef.current?.forEach((text) => {
        const matchingLayer = layers.find((layer) => layer.id === text.id);
        if (matchingLayer) {
          text.textContainer.zIndex = layers.indexOf(matchingLayer);
          app?.stage.sortChildren();
        }
      });
    }
  }, [layers]);
  return null;
};

export default TextRenderer;
